'use client';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormAutocomplete } from '@/components/common/FormAutocomplete';
import type { AvailableTemplate, RecipeFavoriteDto } from '@/api/ApiClient';
import { useBackend } from '@/hooks/useBackend';
import { toRecipeTemplateStep } from '@/lib/utils/recipeTemplateStep';
import { fetchRecipeFavoritesForProcessing } from '@/lib/utils/recipeFavoriteAtCreate';
import { log } from '@/lib/utils/logger';

export type RecipeInitDialogMode = 'initialize' | 'startNew';

export interface RecipeInitDialogProps {
  open: boolean;
  onClose: () => void;
  mode: RecipeInitDialogMode;
  activityType: string;
  /** Technique id or resolved context (e.g. Substrate for prep/post). */
  technique: string | null | undefined;
  instrument: string | null | undefined;
  /** Pre-select when replacing or re-opening after a prior template. */
  initialTemplateName?: string;
  onApply: (templateName: string, favorite: RecipeFavoriteDto | null) => Promise<boolean>;
}

/**
 * Dialog to initialize or start a new activity recipe from a template and/or favorite.
 * Template and favorite are bootstrap inputs only — not persisted activity metadata the user edits elsewhere.
 */
export const RecipeInitDialog: FC<RecipeInitDialogProps> = ({
  open,
  onClose,
  mode,
  activityType,
  technique,
  instrument,
  initialTemplateName,
  onApply,
}) => {
  const backend = useBackend();
  const [templateOptions, setTemplateOptions] = useState<AvailableTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
  const [availableFavorites, setAvailableFavorites] = useState<RecipeFavoriteDto[]>([]);
  const [selectedFavorite, setSelectedFavorite] = useState<RecipeFavoriteDto | null>(null);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const resetForm = useCallback(() => {
    setSelectedTemplateName(null);
    setSelectedFavorite(null);
    setAvailableFavorites([]);
    setTemplateOptions([]);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  useEffect(() => {
    if (!open || !backend) {
      return;
    }

    let cancelled = false;
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const templates = await backend.recipeTemplate.availableTemplates(
          toRecipeTemplateStep(activityType) ?? null,
          technique ?? null,
          instrument ?? null
        );
        if (cancelled) return;
        setTemplateOptions(templates);
        const names = templates.map((t) => t.name).filter((n): n is string => !!n?.trim());
        const initial =
          initialTemplateName && names.includes(initialTemplateName)
            ? initialTemplateName
            : names.length === 1
              ? names[0]
              : initialTemplateName && names.length === 0
                ? initialTemplateName
                : initialTemplateName && !names.includes(initialTemplateName)
                  ? initialTemplateName
                  : null;
        setSelectedTemplateName(initial);
        setSelectedFavorite(null);
      } catch (err) {
        if (!cancelled) {
          log.error('[RecipeInitDialog] Failed to load recipe templates:', err);
          setTemplateOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingTemplates(false);
        }
      }
    };

    void loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [open, backend, activityType, technique, instrument, initialTemplateName]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    const loadFavorites = async () => {
      if (!backend || !selectedTemplateName) {
        if (!cancelled) {
          setAvailableFavorites([]);
        }
        return;
      }

      setLoadingFavorites(true);
      try {
        const favorites = await fetchRecipeFavoritesForProcessing(
          backend,
          technique,
          instrument,
          selectedTemplateName,
          activityType
        );
        if (!cancelled) {
          setAvailableFavorites(favorites);
        }
      } catch (err) {
        if (!cancelled) {
          log.error('[RecipeInitDialog] Failed to load recipe favorites:', err);
          setAvailableFavorites([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFavorites(false);
        }
      }
    };

    void loadFavorites();
    return () => {
      cancelled = true;
    };
  }, [open, backend, selectedTemplateName, technique, instrument, activityType]);

  const templateNameOptions = useMemo(() => {
    const names = templateOptions.map((t) => t.name).filter((n): n is string => !!n?.trim());
    if (selectedTemplateName && !names.includes(selectedTemplateName)) {
      return [...names, selectedTemplateName];
    }
    return names;
  }, [templateOptions, selectedTemplateName]);

  const handleSubmit = async () => {
    const templateName = selectedTemplateName?.trim();
    if (!templateName) {
      return;
    }
    setIsApplying(true);
    try {
      const ok = await onApply(templateName, selectedFavorite);
      if (ok) {
        handleClose();
      }
    } finally {
      setIsApplying(false);
    }
  };

  const title = mode === 'startNew' ? 'Start new recipe' : 'Initialize recipe';
  const submitText = mode === 'startNew' ? 'Start new recipe' : 'Initialize recipe';

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title={title}
      onSubmit={() => void handleSubmit()}
      submitText={submitText}
      submitDisabled={!selectedTemplateName?.trim() || loadingTemplates || isApplying}
      isLoading={isApplying}
      loadingText="Applying..."
      maxWidth="sm"
    >
      {mode === 'startNew' ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Starting a new recipe clears the current recipe contents. Save the activity afterward to persist changes.
        </Alert>
      ) : null}
      <Box display="flex" flexDirection="column" gap={2}>
        <FormAutocomplete
          options={templateNameOptions}
          value={selectedTemplateName}
          inputValue={selectedTemplateName ?? ''}
          onInputChange={(_, value) => setSelectedTemplateName(value || null)}
          getOptionLabel={(option) => String(option)}
          onChange={(_, value) => {
            const name = typeof value === 'string' ? value : value;
            setSelectedTemplateName(name);
            setSelectedFavorite(null);
          }}
          label="Recipe template"
          required
          disabled={loadingTemplates}
          helperText={
            loadingTemplates
              ? 'Loading templates...'
              : templateNameOptions.length === 0
                ? 'No recipe templates available for this technique and instrument'
                : undefined
          }
          autoSelect
          autoHighlight
        />
        <FormAutocomplete
          options={availableFavorites}
          getOptionLabel={(option) => option.name || ''}
          value={selectedFavorite}
          onChange={(_, favorite) => {
            setSelectedFavorite(favorite);
            if (favorite?.template) {
              setSelectedTemplateName(favorite.template);
            } else if (favorite?.recipe?.templateName) {
              setSelectedTemplateName(favorite.recipe.templateName);
            }
          }}
          label="Recipe favorite"
          disabled={!selectedTemplateName || loadingFavorites}
          helperText={
            loadingFavorites
              ? 'Loading favorites...'
              : !selectedTemplateName
                ? 'Select a recipe template first'
                : availableFavorites.length === 0
                  ? 'No favorites for this template'
                  : 'Optional — pre-fill from a saved favorite'
          }
        />
      </Box>
    </StandardDialog>
  );
};
